<template>
  <div class="flex w-fit flex-col items-center gap-3">
    <p v-if="label" :id="labelId" class="text-title-medium text-primary font-medium">
      {{ label }}
    </p>
    <div
      class="flex items-center gap-1.5 sm:gap-2"
      role="group"
      :aria-labelledby="label ? labelId : undefined"
    >
      <template v-for="(char, index) in slots" :key="index">
        <span
          v-if="index === separatorAt"
          class="text-headline-small text-primary/50 px-0.5"
          aria-hidden="true"
          >-</span
        >
        <input
          ref="inputEls"
          :value="char"
          type="text"
          inputmode="text"
          maxlength="1"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="characters"
          spellcheck="false"
          :disabled="disabled"
          :aria-label="cellLabel(index)"
          class="text-headline-medium rounded-large text-primary caret-primary sm:text-headline-medium h-16 w-10 shrink-0 rounded-2xl border-2 p-0 text-center leading-[4rem]! transition-colors focus:outline-none disabled:opacity-50 sm:h-24 sm:w-16 sm:rounded-3xl sm:leading-[6rem]!"
          :class="[
            focusedIndex === index &&
              !hasError &&
              'border-primary bg-surface-container-lowest shadow-1 border-solid',
            focusedIndex !== index &&
              !hasError &&
              !char &&
              'border-primary/50 bg-surface-container-low border-dashed',
            focusedIndex !== index && char && !hasError && 'bg-primary-container',
            hasError && 'border-error bg-error-container text-error!',
          ]"
          @keydown="onKeydown(index, $event)"
          @input="onInput(index, $event)"
          @paste="onPaste(index, $event)"
          @copy="onCopy"
          @focus="onFocus(index)"
          @blur="onBlur(index)"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { nextTick, onMounted, ref, useId, watch } from 'vue'

  const codeLength = 6
  const separatorAt = 3

  interface Props {
    label?: string
    disabled?: boolean
    autofocus?: boolean
    hasError?: boolean
  }

  const props = defineProps<Props>()
  const model = defineModel<string | undefined>()
  const emit = defineEmits<{
    submit: []
  }>()

  const labelId = useId()
  const inputEls = ref<HTMLInputElement[]>([])
  const slots = ref<string[]>(Array.from({ length: codeLength }, () => ''))
  const focusedIndex = ref<number | null>(null)

  const compactSlots = () => slots.value.join('')

  const publish = () => {
    const compact = compactSlots()
    model.value = compact || undefined
  }

  watch(
    () => model.value,
    (value) => {
      const compact = (value ?? '').replace(/[\s-]/g, '').toUpperCase().slice(0, codeLength)
      if (compact === compactSlots()) {
        return
      }
      slots.value = Array.from({ length: codeLength }, (_, index) => compact[index] ?? '')
    },
    { immediate: true },
  )

  const cellLabel = (index: number) => {
    const name = props.label || 'Code'
    return `${name} ${index + 1}`
  }

  const focusAt = (index: number) => {
    const input = inputEls.value[index]
    if (!input || input.disabled) {
      return
    }
    input.focus()
  }

  const writeChar = (index: number, char: string) => {
    const next = [...slots.value]
    next[index] = char.toUpperCase()
    slots.value = next
    publish()
    if (index < codeLength - 1) {
      focusAt(index + 1)
    }
  }

  const writeFrom = (start: number, text: string) => {
    const cleaned = text.replace(/[\s-]/g, '').toUpperCase()
    if (!cleaned) {
      return
    }
    const replaceAll = cleaned.length >= codeLength
    const origin = replaceAll ? 0 : start
    const next = replaceAll ? Array.from({ length: codeLength }, () => '') : [...slots.value]
    const chunk = cleaned.slice(0, codeLength - origin)
    for (let offset = 0; offset < chunk.length; offset++) {
      next[origin + offset] = chunk[offset] ?? ''
    }
    slots.value = next
    publish()
    focusAt(Math.min(origin + chunk.length, codeLength - 1))
  }

  const onKeydown = (index: number, event: KeyboardEvent) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      const next = [...slots.value]
      if (next[index]) {
        next[index] = ''
        slots.value = next
        publish()
        return
      }
      if (index === 0) {
        return
      }
      next[index - 1] = ''
      slots.value = next
      publish()
      focusAt(index - 1)
      return
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusAt(index - 1)
      return
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusAt(index + 1)
      return
    }
    if (event.key === 'Enter' && slots.value.every((char) => char !== '')) {
      event.preventDefault()
      emit('submit')
      return
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return
    }
    if (event.key.length !== 1 || event.key === '-' || /\s/.test(event.key)) {
      return
    }
    event.preventDefault()
    writeChar(index, event.key)
  }

  const onInput = (index: number, event: Event) => {
    const target = event.target
    if (!(target instanceof HTMLInputElement)) {
      return
    }
    const cleaned = target.value.replace(/[\s-]/g, '').toUpperCase()
    if (!cleaned) {
      if (target.value) {
        target.value = slots.value[index] ?? ''
        return
      }
      if ((slots.value[index] ?? '') !== '') {
        const next = [...slots.value]
        next[index] = ''
        slots.value = next
        publish()
      }
      return
    }
    if (cleaned.length > 1) {
      writeFrom(index, cleaned)
      target.value = slots.value[index] ?? ''
      return
    }
    if (slots.value[index] === cleaned) {
      target.value = cleaned
      return
    }
    writeChar(index, cleaned)
    target.value = cleaned
  }

  const onPaste = (index: number, event: ClipboardEvent) => {
    event.preventDefault()
    writeFrom(index, event.clipboardData?.getData('text') ?? '')
  }

  const onCopy = (event: ClipboardEvent) => {
    const compact = compactSlots()
    if (!compact) {
      return
    }
    const formatted =
      compact.length > separatorAt
        ? `${compact.slice(0, separatorAt)}-${compact.slice(separatorAt)}`
        : compact
    event.preventDefault()
    event.clipboardData?.setData('text/plain', formatted)
  }

  const onFocus = (index: number) => {
    focusedIndex.value = index
  }

  const onBlur = (index: number) => {
    if (focusedIndex.value === index) {
      focusedIndex.value = null
    }
  }

  onMounted(() => {
    if (!props.autofocus) {
      return
    }
    nextTick(() => focusAt(0))
  })
</script>
